import { useNavigate } from "react-router-dom";
import { ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import HoloProgress from "./HoloProgress";
import type { LunimarRealm } from "./lunimarRealms";

interface Props {
  realm: LunimarRealm;
  title: string;
  stepIndex: number;
  totalSteps: number;
  onOpenCoach: () => void;
}

/**
 * Premium glass mission-control bar — replaces the flat module header.
 * Title glows in the realm's primary accent, with the realm subtitle and
 * a holographic step badge to the right.
 */
const ModuleRealmHeader = ({
  realm,
  title,
  stepIndex,
  totalSteps,
  onOpenCoach,
}: Props) => {
  const navigate = useNavigate();
  return (
    <header
      className="sticky top-0 z-50 border-b border-white/10 bg-[hsl(230_55%_8%/0.65)] backdrop-blur-xl"
      style={{
        paddingTop: "env(safe-area-inset-top, 0px)",
        paddingLeft: "env(safe-area-inset-left, 0px)",
        paddingRight: "env(safe-area-inset-right, 0px)",
      }}
    >
      <div className="container mx-auto box-border flex max-w-5xl items-center gap-3 px-3 py-2.5 sm:px-5 sm:py-3.5">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/dashboard")}
          className="h-10 w-10 shrink-0 rounded-xl border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white sm:h-9 sm:w-9"
          aria-label="Back to dashboard"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>

        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <h1
              className="truncate font-heading text-sm font-bold leading-tight sm:text-base md:text-lg"
              style={{
                color: `hsl(${realm.primaryAccent})`,
                textShadow: `0 0 18px hsl(${realm.primaryAccent} / 0.55)`,
              }}
            >
              {title}
            </h1>
          </div>
          <p
            className="truncate font-mono text-[10px] uppercase tracking-[0.18em] sm:text-[11px]"
            style={{ color: `hsl(${realm.secondaryAccent} / 0.85)` }}
          >
            {realm.realmName}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <div
            className="hidden rounded-lg border px-2.5 py-1 font-mono text-[11px] tracking-wider sm:flex sm:items-center"
            style={{
              borderColor: `hsl(${realm.primaryAccent} / 0.4)`,
              background: `hsl(${realm.primaryAccent} / 0.08)`,
              color: `hsl(${realm.primaryAccent})`,
              boxShadow: `inset 0 0 12px hsl(${realm.primaryAccent} / 0.18)`,
            }}
          >
            STEP {String(stepIndex + 1).padStart(2, "0")} / {String(totalSteps).padStart(2, "0")}
          </div>
          <div
            className="rounded-lg border px-2 py-1 font-mono text-[10px] tracking-wider sm:hidden"
            style={{
              borderColor: `hsl(${realm.primaryAccent} / 0.4)`,
              background: `hsl(${realm.primaryAccent} / 0.08)`,
              color: `hsl(${realm.primaryAccent})`,
            }}
          >
            {stepIndex + 1}/{totalSteps}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenCoach}
            className="h-10 w-10 rounded-xl border border-white/10 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white sm:h-9 sm:w-9"
            title="AI Coach"
            aria-label="Open AI coach"
          >
            <Sparkles className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="px-3 pb-2 sm:px-5">
        <HoloProgress stepIndex={stepIndex} totalSteps={totalSteps} realm={realm} />
      </div>
    </header>
  );
};

export default ModuleRealmHeader;